import { createTheme, type ThemeOptions } from '@mui/material/styles';
import { semantic, primitive } from './colors';
import { fontFamily, typography } from './typography';
import { radius } from './radius';
import { spacing } from './spacing';
import { shadows } from './shadows';
import { breakpoints } from './breakpoints';
import { zIndex } from './zIndex';

const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: semantic.primary.main,
      light: semantic.primary.light,
      dark: semantic.primary.dark,
      contrastText: semantic.primary.contrast,
    },
    secondary: {
      main: semantic.secondary.main,
      light: semantic.secondary.light,
      dark: semantic.secondary.dark,
      contrastText: semantic.secondary.contrast,
    },
    success: {
      main: semantic.success.main,
      light: semantic.success.light,
      dark: semantic.success.dark,
    },
    warning: {
      main: semantic.warning.main,
      light: semantic.warning.light,
      dark: semantic.warning.dark,
    },
    error: {
      main: semantic.error.main,
      light: semantic.error.light,
      dark: semantic.error.dark,
    },
    info: {
      main: semantic.info.main,
      light: semantic.info.light,
      dark: semantic.info.dark,
    },
    background: {
      default: semantic.background.default,
      paper: semantic.background.paper,
    },
    text: {
      primary: semantic.text.primary,
      secondary: semantic.text.secondary,
      disabled: semantic.text.disabled,
    },
    divider: semantic.border.default,
  },
  shape: {
    borderRadius: radius.md,
  },
  typography: {
    fontFamily: fontFamily.body,
    h1: { ...typography.h1 },
    h2: { ...typography.h2 },
    h3: { ...typography.h3 },
    h4: { ...typography.h4 },
    h5: { fontFamily: fontFamily.heading, fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.35 },
    h6: { fontFamily: fontFamily.heading, fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 },
    body1: { ...typography.body1 },
    body2: { ...typography.body2 },
    caption: { ...typography.caption },
    button: { ...typography.button },
    overline: { ...typography.overline },
    subtitle1: { fontFamily: fontFamily.body, fontSize: '1rem', fontWeight: 500 },
    subtitle2: { fontFamily: fontFamily.body, fontSize: '0.875rem', fontWeight: 600 },
  },
  breakpoints: {
    values: { ...breakpoints },
  },
  zIndex: {
    modal: zIndex.modal,
    snackbar: zIndex.snackbar,
    tooltip: zIndex.tooltip,
    appBar: zIndex.header,
    drawer: zIndex.drawer,
  },
  components: {
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          border: `1px solid ${semantic.border.default}`,
        },
        rounded: { borderRadius: radius.md },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          border: `1px solid ${semantic.border.default}`,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: spacing.lg,
          '&:last-child': {
            paddingBottom: spacing.lg,
          },
        },
      },
    },
    MuiButton: {
      defaultProps: { size: 'small', disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: typography.button.fontWeight,
          borderRadius: radius.md,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        head: {
          fontWeight: typography.label.fontWeight,
          backgroundColor: semantic.neutral[100],
          color: semantic.text.primary,
          fontSize: typography.label.fontSize,
          letterSpacing: typography.label.letterSpacing,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': {
            backgroundColor: `${semantic.primary.main}0A`, // 4% opacity hex
          },
          '&.Mui-selected': {
            backgroundColor: `${semantic.primary.main}14`, // 8% opacity
            borderLeft: `3px solid ${semantic.primary.main}`,
          },
        },
      },
    },
    MuiChip: {
      defaultProps: { size: 'small' },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
    },
    MuiSelect: {
      defaultProps: { size: 'small' },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500 },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: radius.lg,
          boxShadow: shadows.high,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: spacing.lg,
          paddingBottom: spacing.md,
          fontSize: typography.h3.fontSize,
          fontWeight: 600,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: spacing.lg,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: `${spacing.md}px ${spacing.lg}px`,
        },
      },
    },
    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        autoHideDuration: 4000,
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: semantic.border.focus,
            boxShadow: shadows.focus,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: semantic.border.error,
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${semantic.border.default}`,
        },
      },
    },
  },
};

export const theme = createTheme(themeOptions);

/** Dark mode variant (ready for future toggle) */
export const darkThemeOptions: ThemeOptions = {
  ...themeOptions,
  palette: {
    mode: 'dark',
    primary: themeOptions.palette?.primary,
    secondary: themeOptions.palette?.secondary,
    success: themeOptions.palette?.success,
    warning: themeOptions.palette?.warning,
    error: themeOptions.palette?.error,
    info: themeOptions.palette?.info,
    background: {
      default: primitive.charcoal[900],
      paper: primitive.charcoal[800],
    },
    text: {
      primary: primitive.slate[100],
      secondary: primitive.slate[400],
      disabled: primitive.slate[600],
    },
    divider: primitive.charcoal[700],
  },
  components: {
    ...themeOptions.components,
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          border: `1px solid ${primitive.charcoal[700]}`,
          backgroundColor: primitive.charcoal[800],
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          border: `1px solid ${primitive.charcoal[700]}`,
          backgroundColor: primitive.charcoal[800],
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        head: {
          fontWeight: typography.label.fontWeight,
          backgroundColor: primitive.charcoal[700],
          color: primitive.slate[100],
          fontSize: typography.label.fontSize,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${primitive.charcoal[700]}`,
        },
      },
    },
  },
};

export const darkTheme = createTheme(darkThemeOptions);
